import { LazyQueryExecFunction, OperationVariables, QueryResult, useLazyQuery } from "@apollo/client";
import { RefObject, useEffect, useRef } from "react";

// type LazyQueryReturn<TData = any, TVariables extends OperationVariables = OperationVariables> = typeof useLazyQuery<TData, TVariables>;
type LazyQueryParams<TData = any, TVariables extends OperationVariables = OperationVariables> = Parameters<typeof useLazyQuery<TData, TVariables>>;

export const useCancellableLazyQuery = <TData = any, TVariables extends OperationVariables = OperationVariables>(
    query: LazyQueryParams<TData, TVariables>[0],
    options: LazyQueryParams<TData, TVariables>[1],
    fireOnMount?: boolean
): [
    LazyQueryExecFunction<TData, TVariables>,
    QueryResult<TData, TVariables>,
    RefObject<AbortController | null>
] => {
    const abortControllerRef = useRef<AbortController | null>(null);
    const [lazyQuery, result] = useLazyQuery<TData, TVariables>(query, options);

    useEffect(() => {
        if (fireOnMount) lazyQuery();
        return () => abortControllerRef.current?.abort("Component unmounted, cancelling query");
    }, []);

    type LazyQueryOptions = Parameters<typeof lazyQuery>[0];
    const cancellableLazyQuery = (options: LazyQueryOptions) => {
        abortControllerRef.current?.abort("Cancelling previous query");
        abortControllerRef.current = typeof AbortController === "undefined" ? AbortController : new AbortController();
        return lazyQuery({ ...options, context: { queryDeduplication: false, fetchOptions: { signal: abortControllerRef.current?.signal } } });
    };

    return [cancellableLazyQuery, result, abortControllerRef];
};
